import React, { useState, useEffect } from 'react';
import { 
  calculateDocumentTotals, 
  formatCurrency,
  validateItemData 
} from '../utils/calculationUtils';
import axios from 'axios';
import { useAuth } from './AuthContext';
import ActivityLogger from '../utils/activityLogger';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DocumentForm = ({ onDocumentCreated }) => {
  const { user } = useAuth();
  const [docType, setDocType] = useState('devis');
  const [clientsList, setClientsList] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  
  // Nouveaux états pour la liaison avec opportunités et opérations
  const [opportunitiesList, setOpportunitiesList] = useState([]);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState('');
  const [operationsList, setOperationsList] = useState([]);
  const [selectedOperationId, setSelectedOperationId] = useState('');
  
  const [client, setClient] = useState({
    name: '',
    address: '',
    rccm: '',
    contact: '',
    email: '',
    phone: ''
  });
  const [items, setItems] = useState([{
    ref: '',
    description: '',
    quantity: 1,
    unit: 'remorque',
    unit_price: 0,
    total_ht: 0,
    tva_rate: 0,
    total_ttc: 0,
    comment: ''
  }]);
  
  // Nouveaux champs pour les remises
  const [remiseType, setRemiseType] = useState('pourcentage'); // pourcentage ou montant
  const [remiseValue, setRemiseValue] = useState(0);
  const [remiseDescription, setRemiseDescription] = useState('');
  
  const [conditions, setConditions] = useState('');
  const [delaiRetractation, setDelaiRetractation] = useState('30 jours');
  const [objet, setObjet] = useState('');
  const [inclus, setInclus] = useState('');
  const [exclus, setExclus] = useState('');
  const [notes, setNotes] = useState('');
  const [emetteur, setEmetteur] = useState('Baobab Connect');
  const [suiviPar, setSuiviPar] = useState(user?.full_name || '');
  
  // Clauses contractuelles prédéfinies
  const [clausesOffre, setClausesOffre] = useState([
    'Transport de matériels de construction depuis Abidjan à rendu diverses destinations',
    'Franchise chargement 24H',
    'Franchise déchargement 24H',
    'Franchise frontières défi par frontière'
  ]);
  const [clausesExclusions, setClausesExclusions] = useState([
    'TVA 18% applicable en sus',
    'Hors passage frontière, frais de transit, tout statut frais sur le territoire de destination',
    'Les charges liées à une marchandise dangereuse',
    'Hors assurance'
  ]);

  // Charger les clients, opportunités et opérations au montage du composant
  useEffect(() => {
    fetchClients();
    fetchOpportunities();
  }, []);

  // Charger les opérations quand une opportunité est sélectionnée
  useEffect(() => {
    if (selectedOpportunityId) {
      fetchOperationsForOpportunity(selectedOpportunityId);
    } else {
      setOperationsList([]);
      setSelectedOperationId('');
    }
  }, [selectedOpportunityId]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/crm/clients`);
      setClientsList(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchOpportunities = async () => {
    try {
      const response = await axios.get(`${API}/opportunities`);
      setOpportunitiesList(response.data);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    }
  };

  const fetchOperationsForOpportunity = async (opportunityId) => {
    try {
      const response = await axios.get(`${API}/opportunities/${opportunityId}/operations`);
      setOperationsList(response.data);
    } catch (error) {
      console.error('Error fetching operations:', error);
      setOperationsList([]);
    }
  };

  // Fonction pour charger les informations d'un client sélectionné
  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);
    if (clientId) {
      const selectedClient = clientsList.find(c => c.id === clientId);
      if (selectedClient) {
        setClient({
          name: selectedClient.name,
          address: selectedClient.address || '',
          rccm: selectedClient.rccm || '',
          contact: selectedClient.contact_person || '',
          email: selectedClient.email || '',
          phone: selectedClient.phone || ''
        });
      }
    } else {
      // Réinitialiser le formulaire client
      setClient({
        name: '',
        address: '',
        rccm: '',
        contact: '',
        email: '',
        phone: ''
      });
    }
  };

  // Fonction pour gérer la sélection d'opportunité
  const handleOpportunitySelect = (opportunityId) => {
    setSelectedOpportunityId(opportunityId);
    if (opportunityId) {
      const selectedOpportunity = opportunitiesList.find(o => o.id === opportunityId);
      if (selectedOpportunity) {
        // Auto-fill client information from opportunity
        setClient({
          name: selectedOpportunity.client_name,
          address: '',
          rccm: '',
          contact: '',
          email: '',
          phone: ''
        });
        
        // Auto-fill object field for devis
        if (docType === 'devis') {
          setObjet(`Devis pour l'opportunité : ${selectedOpportunity.nom_opportunite}`);
        }
        
        // Find and select the client if exists in clients list
        const clientInList = clientsList.find(c => c.name === selectedOpportunity.client_name);
        if (clientInList) {
          setSelectedClientId(clientInList.id);
          setClient({
            name: clientInList.name,
            address: clientInList.address || '',
            rccm: clientInList.rccm || '',
            contact: clientInList.contact_person || '',
            email: clientInList.email || '',
            phone: clientInList.phone || ''
          });
        }
      }
    } else {
      // Reset when no opportunity selected
      setSelectedClientId('');
      setClient({
        name: '',
        address: '',
        rccm: '',
        contact: '',
        email: '',
        phone: ''
      });
      setObjet('');
    }
  };

  // Fonction pour gérer la sélection d'opération
  const handleOperationSelect = (operationId) => {
    setSelectedOperationId(operationId);
    if (operationId) {
      const selectedOperation = operationsList.find(o => o.id === operationId);
      if (selectedOperation) {
        // Auto-add operation details to items
        const operationDescription = `${selectedOperation.description} - ${selectedOperation.mode_transport} de ${selectedOperation.lieu_collecte} à ${selectedOperation.lieu_livraison}`;
        
        if (items.length === 1 && !items[0].description) {
          // If we have only one empty item, fill it
          updateItem(0, 'description', operationDescription);
          updateItem(0, 'unit_price', selectedOperation.valeur_estimee_xof || 0);
        } else {
          // Add a new item
          const newItem = {
            ref: selectedOperation.numero_operation,
            description: operationDescription,
            quantity: 1,
            unit: 'service',
            unit_price: selectedOperation.valeur_estimee_xof || 0,
            total_ht: selectedOperation.valeur_estimee_xof || 0,
            tva_rate: 0,
            total_ttc: selectedOperation.valeur_estimee_xof || 0,
            comment: `Opération: ${selectedOperation.numero_operation}`
          };
          setItems([...items, newItem]);
        }
      }
    }
  };

  const addItem = () => {
    setItems([...items, {
      ref: '',
      description: '',
      quantity: 1,
      unit: 'remorque',
      unit_price: 0,
      total_ht: 0,
      tva_rate: 0,
      total_ttc: 0,
      comment: ''
    }]);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Calculate totals
    if (field === 'quantity' || field === 'unit_price' || field === 'tva_rate') {
      const quantity = parseFloat(newItems[index].quantity) || 0;
      const unitPrice = parseFloat(newItems[index].unit_price) || 0;
      const tvaRate = parseFloat(newItems[index].tva_rate) || 0;
      
      newItems[index].total_ht = quantity * unitPrice;
      newItems[index].total_ttc = newItems[index].total_ht * (1 + tvaRate / 100);
    }
    
    setItems(newItems);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // FONCTION REMPLACÉE - Utilise maintenant l'utilitaire unifié calculateDocumentTotals  
  const calculateTotals = () => {
    return calculateDocumentTotals(items, {
      mode: 'basic',
      remiseType,
      remiseValue
    });
  };

  // Ajouter une clause à la liste
  const addClause = (type, clause) => {
    if (type === 'offre') {
      setClausesOffre([...clausesOffre, clause]);
    } else {
      setClausesExclusions([...clausesExclusions, clause]);
    }
  };

  // Supprimer une clause
  const removeClause = (type, index) => {
    if (type === 'offre') {
      setClausesOffre(clausesOffre.filter((_, i) => i !== index));
    } else {
      setClausesExclusions(clausesExclusions.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const totals = calculateTotals();
    
    const documentData = {
      type: docType,
      client,
      items,
      conditions,
      delai_retractation: delaiRetractation,
      objet,
      inclus: clausesOffre.join('\n'),
      exclus: clausesExclusions.join('\n'),
      notes,
      emetteur,
      suivi_par: suiviPar,
      // Liaison avec opportunité et opération
      opportunite_id: selectedOpportunityId || null,
      operation_id: selectedOperationId || null,
      remise: {
        type: remiseType,
        value: remiseValue,
        description: remiseDescription,
        montant: totals.remiseMontant
      },
      totals: totals
    };

    try {
      const response = await axios.post(`${API}/documents`, documentData);
      
      // Log the document creation activity
      try {
        await ActivityLogger.documentCreated(user, response.data);
      } catch (logError) {
        console.warn('Failed to log document creation:', logError);
      }
      
      onDocumentCreated(response.data);
      alert('Document créé avec succès!');
      
      // Reset form
      setSelectedClientId('');
      setSelectedOpportunityId('');
      setSelectedOperationId('');
      setClient({
        name: '',
        address: '',
        rccm: '',
        contact: '',
        email: '',
        phone: ''
      });
      setItems([{
        ref: '',
        description: '',
        quantity: 1,
        unit: 'remorque',
        unit_price: 0,
        total_ht: 0,
        tva_rate: 0,
        total_ttc: 0,
        comment: ''
      }]);
      setRemiseType('pourcentage');
      setRemiseValue(0);
      setRemiseDescription('');
      setConditions('');
      setObjet('');
      setNotes('');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la création du document');
    }
  };

  const handlePreview = async () => {
    const totals = calculateTotals();
    
    const documentData = {
      type: docType,
      client,
      items,
      conditions,
      delai_retractation: delaiRetractation,
      objet,
      inclus: clausesOffre.join('\n'),
      exclus: clausesExclusions.join('\n'),
      notes,
      emetteur,
      suivi_par: suiviPar,
      // Liaison avec opportunité et opération
      opportunite_id: selectedOpportunityId || null,
      operation_id: selectedOperationId || null,
      remise: {
        type: remiseType,
        value: remiseValue,
        description: remiseDescription,
        montant: totals.remiseMontant
      },
      totals: totals
    };

    try {
      const response = await axios.post(`${API}/documents/preview`, documentData);
      const pdfData = response.data.pdf_base64;
      
      // Create blob and download
      const byteCharacters = atob(pdfData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la génération du preview');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Créer un nouveau document</h3>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Type de document */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Type de document</label>
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
          >
            <option value="devis">Devis / Cotation</option>
            <option value="bon_commande">Bon de commande</option>
            <option value="facture">Facture</option>
          </select>
        </div>

        {/* Liaison avec Opportunités et Opérations */}
        <div className="bg-green-50 p-4 rounded-lg">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">🎯 Liaison Commerciale</h4>
          
          {/* Sélection d'opportunité */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Opportunité liée (optionnel)
            </label>
            <select
              value={selectedOpportunityId}
              onChange={(e) => handleOpportunitySelect(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
            >
              <option value="">-- Sélectionner une opportunité --</option>
              {opportunitiesList.map((opportunity) => (
                <option key={opportunity.id} value={opportunity.id}>
                  {opportunity.numero_opportunite} - {opportunity.nom_opportunite} ({opportunity.client_name})
                </option>
              ))}
            </select>
            {selectedOpportunityId && (
              <p className="text-sm text-green-600 mt-1">
                ✅ Client et informations remplis automatiquement depuis l'opportunité
              </p>
            )}
          </div>

          {/* Sélection d'opération (si opportunité sélectionnée) */}
          {selectedOpportunityId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Opération liée (optionnel)
              </label>
              <select
                value={selectedOperationId}
                onChange={(e) => handleOperationSelect(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
              >
                <option value="">-- Sélectionner une opération --</option>
                {operationsList.map((operation) => (
                  <option key={operation.id} value={operation.id}>
                    {operation.numero_operation} - {operation.description} ({operation.mode_transport})
                  </option>
                ))}
              </select>
              {selectedOperationId && (
                <p className="text-sm text-green-600 mt-1">
                  ✅ Détails de l'opération ajoutés automatiquement aux articles
                </p>
              )}
              {operationsList.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  Aucune opération trouvée pour cette opportunité
                </p>
              )}
            </div>
          )}
        </div>

        {/* Informations client */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">👤 Informations client</h4>
          
          {/* Note si opportunité sélectionnée */}
          {selectedOpportunityId && (
            <div className="mb-4 p-3 bg-blue-100 rounded-lg">
              <p className="text-sm text-blue-800">
                💡 <strong>Client pré-rempli depuis l'opportunité</strong> - Vous pouvez modifier les informations si nécessaire
              </p>
            </div>
          )}
          
          {/* Sélection de client existant (désactivée si opportunité sélectionnée) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionner un client existant {selectedOpportunityId ? '(désactivé - client depuis opportunité)' : '(optionnel)'}
            </label>
            <select
              value={selectedClientId}
              onChange={(e) => handleClientSelect(e.target.value)}
              disabled={!!selectedOpportunityId}
              className={`w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary ${
                selectedOpportunityId ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            >
              <option value="">-- Sélectionner un client ou saisir manuellement --</option>
              {clientsList.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name} {client.address && `- ${client.address}`}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Nom du client"
              value={client.name}
              onChange={(e) => setClient({...client, name: e.target.value})}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
              required
            />
            <input
              type="text"
              placeholder="Adresse"
              value={client.address}
              onChange={(e) => setClient({...client, address: e.target.value})}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
              required
            />
            <input
              type="text"
              placeholder="RCCM"
              value={client.rccm}
              onChange={(e) => setClient({...client, rccm: e.target.value})}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
            />
            <input
              type="text"
              placeholder="Contact"
              value={client.contact}
              onChange={(e) => setClient({...client, contact: e.target.value})}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
            />
            <input
              type="email"
              placeholder="Email"
              value={client.email}
              onChange={(e) => setClient({...client, email: e.target.value})}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
            />
            <input
              type="tel"
              placeholder="Téléphone"
              value={client.phone}
              onChange={(e) => setClient({...client, phone: e.target.value})}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
            />
          </div>
        </div>

        {/* Informations du commercial */}
        <div className="bg-green-50 p-4 rounded-lg">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Informations du commercial</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Émetteur</label>
              <input
                type="text"
                value={emetteur}
                onChange={(e) => setEmetteur(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Suivi par</label>
              <input
                type="text"
                value={suiviPar}
                onChange={(e) => setSuiviPar(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
                placeholder={user?.full_name || 'Nom du commercial'}
              />
            </div>
          </div>
        </div>

        {/* Champs spécifiques au devis */}
        {docType === 'devis' && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Informations devis</h4>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Objet du devis"
                value={objet}
                onChange={(e) => setObjet(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
              />
              
              {/* Clauses incluses dans l'offre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📋 Clauses incluses dans l'offre
                </label>
                <div className="space-y-2">
                  {clausesOffre.map((clause, index) => (
                    <div key={index} className="flex items-center justify-between bg-green-50 p-2 rounded">
                      <span className="text-sm text-gray-700">• {clause}</span>
                      <button
                        type="button"
                        onClick={() => removeClause('offre', index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Ajouter une clause..."
                      className="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (e.target.value.trim()) {
                            addClause('offre', e.target.value.trim());
                            e.target.value = '';
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        const input = e.target.parentElement.querySelector('input');
                        if (input.value.trim()) {
                          addClause('offre', input.value.trim());
                          input.value = '';
                        }
                      }}
                      className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                    >
                      + Ajouter
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Clauses exclusions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🚫 Clauses d'exclusion
                </label>
                <div className="space-y-2">
                  {clausesExclusions.map((clause, index) => (
                    <div key={index} className="flex items-center justify-between bg-red-50 p-2 rounded">
                      <span className="text-sm text-gray-700">• {clause}</span>
                      <button
                        type="button"
                        onClick={() => removeClause('exclusions', index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Ajouter une exclusion..."
                      className="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (e.target.value.trim()) {
                            addClause('exclusions', e.target.value.trim());
                            e.target.value = '';
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        const input = e.target.parentElement.querySelector('input');
                        if (input.value.trim()) {
                          addClause('exclusions', input.value.trim());
                          input.value = '';
                        }
                      }}
                      className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                    >
                      + Ajouter
                    </button>
                  </div>
                </div>
              </div>
              
              <textarea
                placeholder="Notes générales"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
                rows="3"
              />
            </div>
          </div>
        )}

        {/* Articles */}
        <div>
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Articles / Prestations</h4>
          {items.map((item, index) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                {docType !== 'devis' && (
                  <input
                    type="text"
                    placeholder="Référence"
                    value={item.ref}
                    onChange={(e) => updateItem(index, 'ref', e.target.value)}
                    className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
                  />
                )}
                <input
                  type="text"
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                  className={`p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary ${docType === 'devis' ? 'md:col-span-2' : ''}`}
                  required
                />
                <input
                  type="number"
                  placeholder="Quantité"
                  value={item.quantity}
                  onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                  className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
                  step="0.01"
                  required
                />
                <select
                  value={item.unit}
                  onChange={(e) => updateItem(index, 'unit', e.target.value)}
                  className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
                >
                  <option value="remorque">Remorque</option>
                  <option value="tonne">Tonne</option>
                  <option value="kg">Kg</option>
                  <option value="unité">Unité</option>
                  <option value="m³">m³</option>
                </select>
                <input
                  type="number"
                  placeholder="Prix unitaire"
                  value={item.unit_price}
                  onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                  className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
                  step="0.01"
                  required
                />
                {docType !== 'devis' && (
                  <input
                    type="number"
                    placeholder="TVA %"
                    value={item.tva_rate}
                    onChange={(e) => updateItem(index, 'tva_rate', e.target.value)}
                    className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
                    step="0.01"
                  />
                )}
              </div>
              
              {docType === 'devis' && (
                <div className="mt-2">
                  <textarea
                    placeholder="Commentaire"
                    value={item.comment}
                    onChange={(e) => updateItem(index, 'comment', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
                    rows="2"
                  />
                </div>
              )}
              
              <div className="mt-3 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Total HT: {item.total_ht.toLocaleString()} FCFA
                  {docType !== 'devis' && ` | Total TTC: ${item.total_ttc.toLocaleString()} FCFA`}
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="text-red-600 hover:text-red-800 px-3 py-1 rounded"
                >
                  🗑️ Supprimer
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addItem}
            className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-baobab-primary hover:text-baobab-primary transition-colors"
          >
            + Ajouter un article
          </button>
        </div>

        {/* Conditions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Conditions de règlement</label>
          <textarea
            value={conditions}
            onChange={(e) => setConditions(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
            rows="2"
            placeholder="Ex: Paiement par virement, 50% au chargement, 50% au déchargement"
          />
        </div>

        {/* Délai de rétractation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Délai de rétractation</label>
          <input
            type="text"
            value={delaiRetractation}
            onChange={(e) => setDelaiRetractation(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
            placeholder="30 jours"
          />
        </div>

        {/* Informations complémentaires */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Émetteur</label>
            <input
              type="text"
              value={emetteur}
              onChange={(e) => setEmetteur(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Suivi par</label>
            <input
              type="text"
              value={suiviPar}
              onChange={(e) => setSuiviPar(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
            />
          </div>
        </div>

        {/* Totaux */}
        <div className="bg-baobab-light p-4 rounded-lg">
          <h4 className="text-lg font-semibold text-gray-800 mb-2">📊 Récapitulatif</h4>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Total HT:</span>
              <span className="font-medium">{calculateTotals().totalHT.toLocaleString()} FCFA</span>
            </div>
            <div className="flex justify-between">
              <span>Total TVA:</span>
              <span className="font-medium">{calculateTotals().totalTVA.toLocaleString()} FCFA</span>
            </div>
            <div className="flex justify-between">
              <span>Total TTC:</span>
              <span className="font-medium">{calculateTotals().totalTTC.toLocaleString()} FCFA</span>
            </div>
            
            {/* Affichage des remises */}
            {remiseValue > 0 && (
              <>
                <hr className="my-2 border-gray-300" />
                <div className="flex justify-between text-orange-600">
                  <span>Remise ({remiseValue}{remiseType === 'pourcentage' ? '%' : ' FCFA'}):</span>
                  <span className="font-medium">-{calculateTotals().remiseMontant.toLocaleString()} FCFA</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-baobab-primary">
                  <span>Total après remise:</span>
                  <span>{calculateTotals().totalApresRemiseTTC.toLocaleString()} FCFA</span>
                </div>
              </>
            )}
            
            {/* Si pas de remise, affichage normal */}
            {remiseValue === 0 && (
              <div className="flex justify-between text-lg font-bold text-baobab-primary">
                <span>Total TTC:</span>
                <span>{calculateTotals().totalTTC.toLocaleString()} FCFA</span>
              </div>
            )}
          </div>
        </div>

        {/* Section Remises */}
        <div className="bg-yellow-50 p-4 rounded-lg">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">💰 Remises</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type de remise</label>
              <select
                value={remiseType}
                onChange={(e) => setRemiseType(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
              >
                <option value="pourcentage">Pourcentage (%)</option>
                <option value="montant">Montant fixe (FCFA)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valeur {remiseType === 'pourcentage' ? '(%)' : '(FCFA)'}
              </label>
              <input
                type="number"
                value={remiseValue}
                onChange={(e) => setRemiseValue(parseFloat(e.target.value) || 0)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
                step="0.01"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <input
                type="text"
                value={remiseDescription}
                onChange={(e) => setRemiseDescription(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
                placeholder="Ex: Remise fidélité, Remise volume..."
              />
            </div>
          </div>
          {remiseValue > 0 && (
            <div className="mt-3 p-3 bg-green-100 rounded-lg">
              <p className="text-sm text-green-800">
                💡 Remise appliquée: {remiseValue}{remiseType === 'pourcentage' ? '%' : ' FCFA'} 
                {remiseDescription && ` (${remiseDescription})`}
                = {calculateTotals().remiseMontant.toLocaleString()} FCFA
              </p>
            </div>
          )}
        </div>

        {/* Boutons */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={handlePreview}
            className="flex-1 bg-gray-600 text-white py-3 px-4 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
          >
            👁️ Aperçu PDF
          </button>
          <button
            type="submit"
            className="flex-1 bg-baobab-primary text-white py-3 px-4 rounded-lg hover:bg-baobab-dark focus:outline-none focus:ring-2 focus:ring-baobab-primary transition-colors"
          >
            💾 Créer le document
          </button>
        </div>
      </form>
    </div>
  );
};

export default DocumentForm;